/**
 * Density provider — drives the `compact` class on <html>.
 *
 * The same shape as ThemeProvider, and for the same reasons. THE ACCOUNT IS
 * THE SOURCE OF TRUTH: `users.density` is returned by /auth/me and written
 * through PUT /auth/me/preferences, so the choice follows the person rather
 * than the machine they signed in from.
 *
 * localStorage is a cache with one job — the pre-paint script in index.html
 * reads it to set the class before first paint, so a compact user does not
 * watch the page reflow from comfortable once the profile arrives.
 *
 * What `compact` actually does lives in index.css: it re-scopes the
 * --space-* scale. Nothing here knows a single measurement, which is what
 * keeps the values reviewable in one place.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useAuthStore } from '@/stores/authStore';
import { apiPut } from '@/utils/api';

type Density = 'comfortable' | 'compact';

interface DensityContextValue {
  density: Density;
  setDensity: (d: Density) => void;
}

const DensityContext = createContext<DensityContextValue>({
  density: 'comfortable',
  setDensity: () => {},
});

export function useDensity() {
  return useContext(DensityContext);
}

const DENSITY_STORAGE_KEY = 'spaciora-density';
const VALID: readonly Density[] = ['comfortable', 'compact'];

function cachedDensity(): Density {
  try {
    const v = localStorage.getItem(DENSITY_STORAGE_KEY) as Density | null;
    return v && VALID.includes(v) ? v : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

export default function DensityProvider({ children }: { children: React.ReactNode }) {
  // Seeded from the cache so the first paint matches what the pre-paint script
  // already put on <html>. The account overrides it as soon as it arrives.
  const [density, setDensityState] = useState<Density>(cachedDensity);

  const accountDensity = useAuthStore((s) => s.user?.density) as Density | undefined;

  // The account's value wins whenever the two disagree.
  //
  // This calls setDensityState directly and NOT setDensity, which is what keeps
  // adopting a remote value from being echoed straight back to the server as if
  // the user had just picked it — a pointless write on every sign-in, and on a
  // second device a race that could undo a change made on the first. `density`
  // is deliberately not a dependency: this fires when the ACCOUNT changes.
  useEffect(() => {
    if (!accountDensity || !VALID.includes(accountDensity)) return;
    setDensityState((current) => {
      if (current === accountDensity) return current;
      try {
        localStorage.setItem(DENSITY_STORAGE_KEY, accountDensity);
      } catch {
        /* private browsing — the account still holds it */
      }
      return accountDensity;
    });
  }, [accountDensity]);

  useEffect(() => {
    document.documentElement.classList.toggle('compact', density === 'compact');
  }, [density]);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      /* the PUT below is what actually persists it */
    }
    // Not awaited and not rolled back on failure: the layout has already
    // changed, and reverting it under someone because a request failed is
    // worse than a preference that did not stick. It retries itself the next
    // time they pick one.
    const { user, updateUser } = useAuthStore.getState();
    if (!user) return;
    updateUser({ ...user, density: next });
    void apiPut('/auth/me/preferences', { density: next }).catch(() => {
      /* offline or logged out; the local value stands until next change */
    });
  }, []);

  return (
    <DensityContext.Provider value={{ density, setDensity }}>
      {children}
    </DensityContext.Provider>
  );
}
